import { useState } from "react";
import { useAtomValue } from "jotai";
import { Button, Modal } from "../components";
import { dateToShortTime } from "../lib";
import { listPaymentMethods, PaymentMethod, Session } from "./api";
import { recusAuthStateAtom } from "../vendor";

type Stage = "idle" | "confirming";

export const RECUS_PROFILE_URL = "https://www.rec.us/app/profile";

export function RecusBookButton(props: {
  session: Session;
  smaller?: boolean;
  inactive?: boolean;
  onBook: (session: Session, pm: PaymentMethod) => void;
}) {
  const authState = useAtomValue(recusAuthStateAtom);
  const [stage, setStage] = useState<Stage>("idle");
  const [checkingPm, setCheckingPm] = useState(false);
  const [showNoCardModal, setShowNoCardModal] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.inactive || checkingPm || !authState) return;
    if (stage === "idle") {
      setStage("confirming");
      return;
    }
    setCheckingPm(true);
    try {
      const pms = await listPaymentMethods(authState);
      const primary = pms[0] ?? null;
      if (!primary) {
        setShowNoCardModal(true);
        setStage("idle");
        return;
      }
      setStage("idle");
      props.onBook(props.session, primary);
    } finally {
      setCheckingPm(false);
    }
  };

  const { label, textClass } = buttonContent(
    stage,
    props.session,
    props.smaller,
    checkingPm
  );

  return (
    <>
      <Button
        onClick={handleClick}
        smaller={props.smaller}
        textClass={textClass}
        inert={props.inactive}
      >
        {label}
      </Button>
      {showNoCardModal && (
        <NoCardModal close={() => setShowNoCardModal(false)} />
      )}
    </>
  );
}

export function NoCardModal(props: { close: () => void }) {
  return (
    <Modal>
      <p className="text-lg mb-6 text-center">
        add a payment method on{" "}
        <a
          className="underline"
          href={RECUS_PROFILE_URL}
          target="_blank"
          rel="noreferrer"
          onClick={props.close}
        >
          rec.us
        </a>{" "}
        first!
      </p>
      <div className="flex flex-row justify-center">
        <Button textClass="text-lg" onClick={props.close}>
          got it!
        </Button>
      </div>
    </Modal>
  );
}

function buttonContent(
  stage: Stage,
  session: Session,
  smaller: boolean | undefined,
  checking: boolean
): { label: string; textClass: string } {
  const priceDollars = Math.round(session.priceCents / 100);
  const smallText = smaller ? "text-sm" : "text-base";
  const bigText = "text-2xl";
  if (checking) {
    return { label: "…", textClass: smallText };
  }
  if (stage === "confirming") {
    return {
      label: smaller ? `book? ($${priceDollars})` : `book this? ($${priceDollars})`,
      textClass: smallText,
    };
  }
  return { label: dateToShortTime(session.startsAt), textClass: bigText };
}
